/* ===========================================================================
   OB DATES - THE ONE SOURCE for the arrival range and the same-day cut off.

   WHY THIS FILE EXISTS. The arrival range and the 7pm countdown were computed
   inside assets/ob-pdp-test1.js and assets/ob-pdp-test3.js, which only load on a
   product page. The cart drawer shows both, and it opens from the header on EVERY
   page, so it cannot reach them. The choice was to copy the logic or to move it.
   Copying it is how a store ends up promising two different dates for one order,
   so it MOVED here, and the PDP now calls these functions rather than owning them.
   There is no second implementation anywhere. Do not write one.

   THE DELIVERY ESTIMATE IS ARCHIE'S, NOT OURS. O&B 7 (194216853850) and PERF
   (194219671898) both compute it as a plain setDate(getDate() + N) in
   sections/product.liquid - no weekend rule, no cut-off, no timezone handling,
   start date = the viewer's today, min 3 and max 8. It used to add BUSINESS days
   over 6 and 10, which put us four days later than the live PDP and later than the
   drawer the customer landed in: a promise mismatch, not a formatting one. Any
   change to how this is computed, formatted or worded needs his approval, and if
   ours ever differs from live on the same product on the same day, ours is wrong.

   LOADED FROM THE LAYOUT HEAD with `defer`, before every consumer. Deferred
   scripts run in document order, and the arm scripts are deferred in the BODY, so
   this is always defined first. If it is ever moved after them, the PDP loses its
   delivery line - so the consumers check for it rather than assuming.
   =========================================================================== */
window.OB = window.OB || {};

(function (OB) {
  'use strict';

  /* CALENDAR days, deliberately. See the note above - this is the live rule. */
  OB.addCalendarDays = function (from, n) {
    var out = new Date(from.getTime());
    out.setDate(out.getDate() + n);
    return out;
  };

  OB.formatDate = function (d) {
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  /* "Sep 24 - Sep 29". A PLAIN HYPHEN with single spaces, which is what both the
     PDP and the cart boards draw - not an en dash, whatever a Figma layer NAME
     may say. Returns null when there is no estimate, so the caller can keep its
     own copy rather than print a broken range. */
  OB.deliveryRange = function (min, max, now) {
    min = parseInt(min, 10);
    max = parseInt(max, 10);
    if (isNaN(min) || isNaN(max)) { return null; }
    var base = now || new Date();
    return OB.formatDate(OB.addCalendarDays(base, min)) + ' - ' +
           OB.formatDate(OB.addCalendarDays(base, max));
  };

  /* THE CUT OFF IS IN THE STORE'S ZONE, NOT THE VIEWER'S.
     setHours() resolves in whatever zone the browser is in, so a 19:00 cut off
     labelled "7pm ET" was counted against 7pm LOCAL. In London the row vanished at
     19:00 BST, five hours early. Worse the other way: a US Pacific shopper kept
     seeing "order within..." until 7pm PT, three hours AFTER the real ET cut off
     had passed, which is a claim the store cannot keep. Intl carries DST itself,
     so EST and EDT both resolve correctly.
     Returns null with no reliable clock - say nothing rather than guess. */
  OB.CUTOFF_ZONE = 'America/New_York';

  OB.minutesUntilCutoff = function (hour, zone) {
    hour = parseInt(hour, 10);
    if (isNaN(hour) || hour < 0 || hour > 23) { return null; }
    var parts;
    try {
      parts = new Intl.DateTimeFormat('en-US', {
        timeZone: zone || OB.CUTOFF_ZONE, hour12: false,
        hour: '2-digit', minute: '2-digit'
      }).formatToParts(new Date());
    } catch (e) {
      return null;
    }
    var hh = null, mm = null;
    for (var i = 0; i < parts.length; i++) {
      if (parts[i].type === 'hour') { hh = parseInt(parts[i].value, 10); }
      if (parts[i].type === 'minute') { mm = parseInt(parts[i].value, 10); }
    }
    if (hh === null || mm === null || isNaN(hh) || isNaN(mm)) { return null; }
    if (hh === 24) { hh = 0; }   /* some engines render midnight as 24 */
    return (hour * 60) - (hh * 60 + mm);
  };

  /* "3h 42m", or "42m" inside the last hour. */
  OB.formatRemaining = function (minutes) {
    if (minutes === null || minutes <= 0) { return null; }
    var h = Math.floor(minutes / 60);
    var m = minutes % 60;
    return h > 0 ? (h + 'h ' + m + 'm') : (m + 'm');
  };

  /* WHICH CUT OFF IS NEXT - TEST 3 ONLY (1 Oct 2026). The arm 3 copy said "today's cut
     off" at 9pm ET and all weekend. This is the ONE rule every arm 3 place reads (the
     announcement bar, the footer USP band, the PDP row and the cart drawer), so they
     can never disagree. All in America/New_York via Intl, so EST and EDT both resolve.
       Mon-Fri before the hour      -> 'today', with minutesLeft to the hour
       Mon-Thu from the hour        -> 'tomorrow', no countdown
       Fri from the hour, Sat, Sun  -> 'monday', no countdown
     The countdown is HIDDEN outside 'today' on purpose: a figure over 12 hours is not
     urgency, and counting across a weekend is not honest. Public holidays are not
     known here. Returns null with no reliable clock - callers keep their fallback. */
  var WEEKDAYS = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

  function zonedParts(now, zone) {
    if (!now || typeof now.getTime !== 'function' || isNaN(now.getTime())) { return null; }
    var parts;
    try {
      parts = new Intl.DateTimeFormat('en-US', {
        timeZone: zone, weekday: 'short', hourCycle: 'h23',
        hour: '2-digit', minute: '2-digit'
      }).formatToParts(now);
    } catch (e) {
      return null;
    }
    var out = { dow: null, h: null, m: null };
    for (var i = 0; i < parts.length; i++) {
      if (parts[i].type === 'weekday' && WEEKDAYS.hasOwnProperty(parts[i].value)) {
        out.dow = WEEKDAYS[parts[i].value];
      }
      if (parts[i].type === 'hour') { out.h = parseInt(parts[i].value, 10); }
      if (parts[i].type === 'minute') { out.m = parseInt(parts[i].value, 10); }
    }
    if (out.dow === null || out.h === null || out.m === null || isNaN(out.h) || isNaN(out.m)) {
      return null;
    }
    if (out.h === 24) { out.h = 0; }   /* some engines render midnight as 24 */
    return out;
  }

  OB.cutoffState = function (hour, now, zone) {
    hour = parseInt(hour, 10);
    if (isNaN(hour) || hour < 0 || hour > 23) { return null; }
    var z = zonedParts(now || new Date(), zone || OB.CUTOFF_ZONE);
    if (!z) { return null; }
    var left = (hour * 60) - (z.h * 60 + z.m);
    var weekday = z.dow >= 1 && z.dow <= 5;
    if (weekday && left > 0) { return { when: 'today', minutesLeft: left, day: "today's" }; }
    if (z.dow >= 1 && z.dow <= 4) { return { when: 'tomorrow', minutesLeft: null, day: "tomorrow's" }; }
    return { when: 'monday', minutesLeft: null, day: "Monday's" };
  };

  /* The one sentence. 'today' needs the label ("7pm ET") and reads exactly as the bar
     always has; the other two need none. */
  OB.cutoffLine = function (state, label) {
    if (!state) { return null; }
    if (state.when === 'today') {
      return label ? ('Order by ' + label + " to make today's cut off") : null;
    }
    return 'Order now to make ' + state.day + ' cut off';
  };

  /* SERVER TEXT IS THE FALLBACK. Elements marked data-ob-cutoff-copy (rendered on arm 3
     only, so no other arm has any) keep their Liquid text on 'today', and are swapped
     only outside it. The original is kept in data-ob-cutoff-orig so a page left open
     overnight goes back to it. data-ob-cutoff-hold pins the element to the width it
     had, centred, so the shorter line does not pull its neighbours sideways. */
  OB.paintCutoffCopy = function (doc, now) {
    if (!doc || typeof doc.querySelectorAll !== 'function') { return; }
    var els = doc.querySelectorAll('[data-ob-cutoff-copy]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var st = OB.cutoffState(el.getAttribute('data-ob-cutoff-hour'), now);
      if (!st) { continue; }
      var orig = el.getAttribute('data-ob-cutoff-orig');
      if (orig === null) { orig = el.textContent; }
      var kind = el.getAttribute('data-ob-cutoff-copy');
      var next;
      if (st.when === 'today') { next = orig; }
      else if (kind === 'title') { next = 'ORDER NOW'; }
      else if (kind === 'sub') { next = 'Makes ' + st.day + ' cut off'; }
      else { next = OB.cutoffLine(st); }
      if (next === null || next === el.textContent) { continue; }
      if (el.getAttribute('data-ob-cutoff-orig') === null) {
        el.setAttribute('data-ob-cutoff-orig', orig);
      }
      if (el.hasAttribute('data-ob-cutoff-hold') && !el.style.minWidth) {
        var w = el.getBoundingClientRect().width;
        if (w > 0) {
          el.style.minWidth = w + 'px';
          el.style.textAlign = 'center';
        }
      }
      el.textContent = next;
    }
  };

  /* Deferred from the head, so the document is parsed by now. Nothing to do, and no
     timer, on any page without a marked element - i.e. on every arm but 3. */
  if (typeof document !== 'undefined' && document &&
      typeof document.querySelector === 'function' &&
      document.querySelector('[data-ob-cutoff-copy]')) {
    OB.paintCutoffCopy(document);
    setInterval(function () { OB.paintCutoffCopy(document); }, 60000);
  }
}(window.OB));
